import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { BREVO_CONFIG } from '../../config/email';
import {
  FaUser,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaClock,
  FaSuitcase,
  FaBabyCarriage,
  FaSkiing,
  FaBaby,
  FaCreditCard,
  FaDog
} from 'react-icons/fa';
import './Quote.css';
import { CARS } from '../../data/cars';

// Extras
const EXTRAS = [
  { id: 'luggage', icon: FaSuitcase, label: 'Valises' },
  { id: 'babyStroller', icon: FaBabyCarriage, label: 'Poussette' },
  { id: 'skiEquipment', icon: FaSkiing, label: 'Équipement Ski' },
  { id: 'babySeat', icon: FaBaby, label: 'Siège Bébé' },
];

// Additional Options
const ADDITIONAL_OPTIONS = [
  { id: 'paymentTerminal', icon: FaCreditCard, label: 'Terminal de Paiement' },
  { id: 'petTransport', icon: FaDog, label: "Transport d'Animal" }
];

// Pricing data
const PRICING = {
  extras: {
    luggage: 1,        // 1 CHF per luggage
    babyStroller: 1,   // 1 CHF per stroller
    skiEquipment: 1,   // 1 CHF per ski set
    babySeat: 5,       // 5 CHF per baby seat
  },
  options: {
    petTransport: 6,       // 6 CHF per pet
    paymentTerminal: 0.025 // 2.5% fee
  }
};

function Quote() {
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(1);

  // All form data in a single state object
  const [formData, setFormData] = useState({
    // Personal Info
    fullName: '',
    phone: '',
    email: '',

    // Trip Details
    kilometers: '',
    date: '',
    time: '',
    passengers: 1,

    // Vehicle & Extras
    selectedCar: '',
    extras: {
      luggage: 0,
      babyStroller: 0,
      skiEquipment: 0,
      babySeat: 0,
    },
    // Additional Options
    options: {
      paymentTerminal: false,
      petTransport: false
    }
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (location.state?.selectedCar) {
      setFormData((prev) => ({
        ...prev,
        selectedCar: location.state.selectedCar,
      }));
    }
  }, [location.state]);

  // Validate only the fields for the current step
  const validateStep = () => {
    const newErrors = {};

    switch (currentStep) {
      case 1:
        if (!formData.fullName.trim()) newErrors.fullName = 'Nom complet requis';
        if (!formData.phone.trim()) newErrors.phone = 'Téléphone requis';
        if (!formData.email.trim()) {
          newErrors.email = 'Email requis';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
          newErrors.email = 'Email invalide';
        }
        break;

      case 2:
        if (!formData.kilometers) {
          newErrors.kilometers = 'Distance requise';
        } else if (isNaN(formData.kilometers) || formData.kilometers <= 0) {
          newErrors.kilometers = 'Distance invalide';
        }
        if (!formData.date) newErrors.date = 'Date requise';
        if (!formData.time) newErrors.time = 'Heure requise';
        break;

      case 3:
        if (!formData.selectedCar) newErrors.selectedCar = 'Veuillez sélectionner un véhicule';
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes for regular text fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  // Handle + and - for extras
  const handleExtraChange = (extraId, change) => {
    setFormData((prev) => ({
      ...prev,
      extras: {
        ...prev.extras,
        [extraId]: Math.max(0, prev.extras[extraId] + change),
      },
    }));
  };

  // Toggle additional options
  const handleOptionToggle = (optionId) => {
    setFormData((prev) => ({
      ...prev,
      options: {
        ...prev.options,
        [optionId]: !prev.options[optionId]
      }
    }));
  };

  // Next step button
  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
      window.scrollTo(0, 0);
    }
  };

  // Back step button
  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };

  // Final submission on Step 3
  const handleSubmit = async () => {
    // Make sure Step 3 is valid before going to 4
    if (validateStep()) {
      try {
        const pricing = calculateTotalPrice();
        const selectedCar = CARS.find((car) => car.id === formData.selectedCar);

        // Format the date in French
        const formattedDate = new Date(formData.date).toLocaleDateString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });

        // Format extras for email with prices
        const extrasText = Object.entries(formData.extras)
          .filter(([_, quantity]) => quantity > 0)
          .map(([key, quantity]) => {
            const extra = EXTRAS.find(e => e.id === key);
            const price = PRICING.extras[key] * quantity;
            return `${extra.label}: ${quantity} (${price.toFixed(2)} CHF)`;
          })
          .join('\n');

        // Format options for email with prices
        const optionsText = Object.entries(formData.options)
          .filter(([_, isSelected]) => isSelected)
          .map(([key, _]) => {
            const option = ADDITIONAL_OPTIONS.find(o => o.id === key);
            const price = key === 'petTransport' 
              ? `${PRICING.options.petTransport} CHF`
              : `${(pricing.basePrice * PRICING.options.paymentTerminal).toFixed(2)} CHF (2.5%)`;
            return `${option.label}: ${price}`;
          })
          .join('\n');

        // Format email HTML content
        const emailHtml = `
          <html>
            <body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
              <h2 style="color: #2c3e50;">Nouvelle demande de devis</h2>
              
              <h3 style="color: #34495e; border-bottom: 2px solid #3498db; padding-bottom: 5px;">INFORMATIONS CLIENT:</h3>
              <p><strong>Nom:</strong> ${formData.fullName}</p>
              <p><strong>Email:</strong> ${formData.email}</p>
              <p><strong>Téléphone:</strong> ${formData.phone}</p>
              
              <h3 style="color: #34495e; border-bottom: 2px solid #3498db; padding-bottom: 5px; margin-top: 20px;">DÉTAILS DU TRAJET:</h3>
              <p><strong>Date:</strong> ${formattedDate}</p>
              <p><strong>Heure:</strong> ${formData.time}</p>
              <p><strong>Distance:</strong> ${formData.kilometers} km</p>
              <p><strong>Prix par km:</strong> ${selectedCar.pricePerKm.toFixed(2)} CHF/km</p>
              
              <h3 style="color: #34495e; border-bottom: 2px solid #3498db; padding-bottom: 5px; margin-top: 20px;">VÉHICULE:</h3>
              <p>${selectedCar.name}</p>
              
              <h3 style="color: #34495e; border-bottom: 2px solid #3498db; padding-bottom: 5px; margin-top: 20px;">EXTRAS:</h3>
              <p>${extrasText || 'Aucun extra'}</p>
              
              <h3 style="color: #34495e; border-bottom: 2px solid #3498db; padding-bottom: 5px; margin-top: 20px;">OPTIONS:</h3>
              <p>${optionsText || 'Aucune option'}</p>
              
              <h3 style="color: #34495e; border-bottom: 2px solid #3498db; padding-bottom: 5px; margin-top: 20px;">TARIFICATION:</h3>
              <p><strong>Prix de base:</strong> ${pricing.basePrice.toFixed(2)} CHF</p>
              <p><strong>Coût des extras:</strong> ${pricing.extrasCost > 0 ? pricing.extrasCost.toFixed(2) : '0.00'} CHF</p>
              <p><strong>Coût transport animal:</strong> ${pricing.petCost > 0 ? pricing.petCost.toFixed(2) : '0.00'} CHF</p>
              <p><strong>Frais terminal:</strong> ${pricing.terminalFee > 0 ? pricing.terminalFee.toFixed(2) : '0.00'} CHF</p>
              <hr style="border: 2px solid #3498db; margin: 20px 0;">
              <h2 style="color: #e74c3c;"><strong>TOTAL: ${pricing.total.toFixed(2)} CHF</strong></h2>
            </body>
          </html>
        `;

        // Format email text content (fallback)
        const emailText = `
Nouvelle demande de devis

INFORMATIONS CLIENT:
Nom: ${formData.fullName}
Email: ${formData.email}
Téléphone: ${formData.phone}

DÉTAILS DU TRAJET:
Date: ${formattedDate}
Heure: ${formData.time}
Distance: ${formData.kilometers} km
Prix par km: ${selectedCar.pricePerKm.toFixed(2)} CHF/km

VÉHICULE:
${selectedCar.name}

EXTRAS:
${extrasText || 'Aucun extra'}

OPTIONS:
${optionsText || 'Aucune option'}

TARIFICATION:
Prix de base: ${pricing.basePrice.toFixed(2)} CHF
Coût des extras: ${pricing.extrasCost > 0 ? pricing.extrasCost.toFixed(2) : '0.00'} CHF
Coût transport animal: ${pricing.petCost > 0 ? pricing.petCost.toFixed(2) : '0.00'} CHF
Frais terminal: ${pricing.terminalFee > 0 ? pricing.terminalFee.toFixed(2) : '0.00'} CHF
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
TOTAL: ${pricing.total.toFixed(2)} CHF
        `.trim();

        // Send email using Brevo API
        const response = await fetch(BREVO_CONFIG.API_URL, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'api-key': BREVO_CONFIG.API_KEY
          },
          body: JSON.stringify({
            sender: {
              name: BREVO_CONFIG.FROM_NAME,
              email: BREVO_CONFIG.FROM_EMAIL
            },
            to: [
              {
                email: BREVO_CONFIG.RECIPIENT_EMAIL,
                name: 'TaxiVD'
              }
            ],
            replyTo: {
              email: formData.email,
              name: formData.fullName
            },
            subject: `Nouvelle demande de devis de ${formData.fullName}`,
            htmlContent: emailHtml,
            textContent: emailText
          })
        });

        if (response.ok) {
          const result = await response.json();
          console.log('Quote sent successfully:', result);
          setCurrentStep(4);
          window.scrollTo(0, 0);
        } else {
          const errorData = await response.json();
          console.error('Failed to send quote:', errorData);
          throw new Error(errorData.message || 'Failed to send email');
        }
      } catch (error) {
        console.error('Failed to send quote:', error);
        alert('Une erreur est survenue lors de l\'envoi. Veuillez réessayer.\n\nErreur: ' + (error.message || 'Unknown error'));
      }
    }
  };

  // Calculate the total price
  const calculateTotalPrice = () => {
    const selectedCar = CARS.find((car) => car.id === formData.selectedCar);
    if (!selectedCar) return null;

    // Base price = pricePerKm * kilometers
    const basePrice = selectedCar.pricePerKm * parseFloat(formData.kilometers || 0);

    // Extras cost
    const extrasCost = Object.entries(formData.extras).reduce((total, [key, quantity]) => {
      return total + PRICING.extras[key] * quantity;
    }, 0);

    // Pet transport cost
    const petCost = formData.options.petTransport ? PRICING.options.petTransport : 0;

    // Payment terminal fee (calculated on base price only)
    const terminalFee = formData.options.paymentTerminal
      ? basePrice * PRICING.options.paymentTerminal
      : 0;

    // Subtotal
    const subtotal = basePrice + extrasCost + petCost;

    // Total
    const total = subtotal + terminalFee;

    return {
      basePrice,
      extrasCost,
      petCost,
      terminalFee,
      total,
      details: {
        distance: parseFloat(formData.kilometers || 0),
        pricePerKm: selectedCar.pricePerKm,
        extras: Object.entries(formData.extras)
          .filter(([_, quantity]) => quantity > 0)
          .map(([key, quantity]) => ({
            name: EXTRAS.find((e) => e.id === key)?.label,
            quantity,
            unitPrice: PRICING.extras[key],
            total: PRICING.extras[key] * quantity
          })),
        options: {
          petTransport: formData.options.petTransport ? PRICING.options.petTransport : 0,
          terminalFee: terminalFee
        }
      }
    };
  };

  // Render summary of prices
  const renderPriceSummary = () => {
    const pricing = calculateTotalPrice();
    if (!pricing) return null;

    return (
      <div className="price-summary">
        <h3>Résumé des Prix</h3>

        <div className="summary-item">
          <span>
            Prix de base ({pricing.details.distance} km × {pricing.details.pricePerKm.toFixed(2)} CHF/km)
          </span>
          <span>{pricing.basePrice.toFixed(2)} CHF</span>
        </div>

        {pricing.details.extras.map((extra, index) => (
          <div key={index} className="summary-item">
            <span>
              {extra.name} ({extra.quantity} × {extra.unitPrice} CHF)
            </span>
            <span>{extra.total.toFixed(2)} CHF</span>
          </div>
        ))}

        {pricing.petCost > 0 && (
          <div className="summary-item">
            <span>Transport d'animal</span>
            <span>{pricing.petCost.toFixed(2)} CHF</span>
          </div>
        )}

        {pricing.terminalFee > 0 && (
          <div className="summary-item">
            <span>Frais terminal de paiement (2.5%)</span>
            <span>{pricing.terminalFee.toFixed(2)} CHF</span>
          </div>
        )}

        <div className="summary-total">
          <span>Total</span>
          <span>{pricing.total.toFixed(2)} CHF</span>
        </div>
      </div>
    );
  };

  // Step 1: Personal info
  const renderPersonalInfo = () => (
    <div className="form-section">
      <h2>Vos Informations</h2>

      <div className="form-grid">
        <div className="form-group full-width">
          <label>
            <FaUser />
            Nom complet
          </label>
          <input
            type="text"
            name="fullName"
            value={formData.fullName}
            onChange={handleInputChange}
            placeholder="Votre prénom et nom"
          />
          {errors.fullName && <span className="error">{errors.fullName}</span>}
        </div>

        <div className="form-group">
          <label>
            <FaPhone />
            Téléphone
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder="Votre numéro"
          />
          {errors.phone && <span className="error">{errors.phone}</span>}
        </div>

        <div className="form-group">
          <label>
            <FaEnvelope />
            Email
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Votre email"
          />
          {errors.email && <span className="error">{errors.email}</span>}
        </div>
      </div>
    </div>
  );

  // Step 2: Trip Details
  const renderTripDetails = () => (
    <div className="form-section">
      <h2>Détails du Trajet</h2>

      <div className="form-grid">
        <div className="form-group">
          <label>
            <FaMapMarkerAlt />
            Distance (km)
          </label>
          <input
            type="number"
            name="kilometers"
            value={formData.kilometers}
            onChange={handleInputChange}
            placeholder="Distance en kilomètres"
            min="1"
          />
          {errors.kilometers && <span className="error">{errors.kilometers}</span>}
        </div>

        <div className="form-group">
          <label>
            <FaCalendarAlt />
            Date
          </label>
          <input
            type="date"
            name="date"
            value={formData.date}
            onChange={handleInputChange}
          />
          {errors.date && <span className="error">{errors.date}</span>}
        </div>

        <div className="form-group">
          <label>
            <FaClock />
            Heure
          </label>
          <input
            type="time"
            name="time"
            value={formData.time}
            onChange={handleInputChange}
          />
          {errors.time && <span className="error">{errors.time}</span>}
        </div>
      </div>
    </div>
  );

  // Step 3: Vehicle + Extras
  const renderVehicleSelection = () => (
    <div className="form-section">
      <h2>Sélection du Véhicule</h2>

      <div className="vehicles-grid">
        {CARS.map((car) => (
          <div
            key={car.id}
            className={`vehicle-card ${formData.selectedCar === car.id ? 'selected' : ''}`}
            onClick={() => {
              setFormData((prev) => ({ ...prev, selectedCar: car.id }));
              setErrors((prev) => ({ ...prev, selectedCar: '' }));
            }}
          >
            <img src={car.image} alt={car.name} />
            <h3>{car.name}</h3>
            <div className="vehicle-details">
              <span>{car.seats} passagers</span>
              <span>{car.baggage} bagages</span>
              <span>CHF {car.pricePerKm.toFixed(2)}/km</span>
            </div>
          </div>
        ))}
      </div>
      {errors.selectedCar && <span className="error centered">{errors.selectedCar}</span>}

      <h3 className="extras-title">Options Supplémentaires</h3>
      <div className="extras-grid">
        {EXTRAS.map((extra) => (
          <div key={extra.id} className="extra-card">
            <extra.icon className="extra-icon" />
            <span className="extra-label">{extra.label}</span>
            <div className="quantity-control">
              <button
                type="button"
                onClick={() => handleExtraChange(extra.id, -1)}
                className="quantity-btn"
              >
                -
              </button>
              <span className="quantity">{formData.extras[extra.id]}</span>
              <button
                type="button"
                onClick={() => handleExtraChange(extra.id, 1)}
                className="quantity-btn"
              >
                +
              </button>
            </div>
          </div>
        ))}
      </div>

      <h3 className="extras-title">Services Additionnels</h3>
      <div className="additional-options-grid">
        {ADDITIONAL_OPTIONS.map((option) => (
          <div
            key={option.id}
            className={`option-card ${formData.options[option.id] ? 'selected' : ''}`}
            onClick={() => handleOptionToggle(option.id)}
          >
            <option.icon className="option-icon" />
            <span className="option-label">{option.label}</span>
            <div
              className="option-checkbox"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={formData.options[option.id]}
                onChange={() => handleOptionToggle(option.id)}
              />
            </div>
          </div>
        ))}
      </div>

      {formData.selectedCar && renderPriceSummary()}
    </div>
  );

  // Step 4: Confirmation
  const renderConfirmation = () => (
    <div className="confirmation-section">
      <div className="confirmation-icon">✓</div>
      <h2>Demande Envoyée!</h2>
      <p>
        Merci pour votre demande de devis. Nous vous contacterons dans les plus brefs délais
        avec une offre personnalisée.
      </p>
      <button
        type="button"
        className="button primary"
        onClick={() => (window.location.href = '/')}
      >
        Retour à l'accueil
      </button>
    </div>
  );

  return (
    <div className="quote-new-page">
      <div className="container">
        <h1>Demande de Devis</h1>

        {/* Progress Bar */}
        <div className="progress-bar">
          {[1, 2, 3, 4].map((step) => (
            <div
              key={step}
              className={`progress-step ${
                step === currentStep ? 'active' : ''
              } ${step < currentStep ? 'completed' : ''}`}
            >
              <div className="step-number">{step}</div>
              <div className="step-label">
                {step === 1 && 'Informations'}
                {step === 2 && 'Trajet'}
                {step === 3 && 'Véhicule'}
                {step === 4 && 'Confirmation'}
              </div>
            </div>
          ))}
        </div>

        {/* We remove onSubmit and prevent Enter from submitting */}
        <form
          className="quote-form"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
        >
          {currentStep === 1 && renderPersonalInfo()}
          {currentStep === 2 && renderTripDetails()}
          {currentStep === 3 && renderVehicleSelection()}
          {currentStep === 4 && renderConfirmation()}

          {/* Navigation Buttons (Hide if on Step 4) */}
          {currentStep < 4 && (
            <div className="form-navigation">
              {currentStep > 1 && (
                <button
                  type="button"
                  className="button secondary"
                  onClick={handleBack}
                >
                  Retour
                </button>
              )}

              {currentStep === 3 ? (
                <button
                  type="button"
                  className="button primary"
                  onClick={handleSubmit}
                >
                  Envoyer la demande
                </button>
              ) : (
                <button
                  type="button"
                  className="button primary"
                  onClick={handleNext}
                >
                  Suivant
                </button>
              )}
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default Quote;
